import React, { createContext, useState, useCallback, useRef, useEffect, useMemo } from "react";
import { IAccessDataProvider } from "../interfaces/IAccessDataProvider";
import { IUserData } from "../interfaces/IUserData";
import { userRoute } from "../helpers/apiRoutes";

export const AccessDataContext = createContext<IAccessDataProvider>({
  user: null,
  loginUser: null,
  logoutUser: null,
  registerUser: null,
  verifyUser: null,
  forgotPassword: null,
  resetPassword: null,
  securityRoles: null,
  isAuthenticated: false
});

const AccessDataProvider = ({ children }) => {
  const userRef = useRef<IUserData | null>(
    JSON.parse(localStorage.getItem("elUser"))
  );
  const [user, setUser] = useState<IUserData | null>(userRef.current);
  const [securityRoles, setSecurityRoles] = useState<string[] | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    userRef.current !== null
  );

  const logoutUser = useCallback(async () => {
    try {
      await fetch(userRoute + "/revoke-token", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Error revoking token:", error);
    } finally {
      userRef.current = null;
      setUser(null);
      setSecurityRoles(null);
      setIsAuthenticated(false);
      localStorage.removeItem("elUser");
      document.cookie = "accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie = "refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }
  }, []);

  const fetchUserRoles = useCallback(async () => {
    try {
      const res = await fetch(userRoute + "/get-security-levels", {
        method: "GET",
        credentials: "include",
      });
      if (res.status === 401) {
        logoutUser();
        return null;
      }
      const roles = await res.json();
      if (roles) setSecurityRoles(roles);
    } catch (error) {
      console.error("Failed to fetch security roles:", error);
    }
  }, [logoutUser]);

  const fetchUserDetails = useCallback(async () => {
    try {
      const res = await fetch(userRoute + "/getUserDetails", {
        method: "GET",
        credentials: "include",
      });
      if (res.status === 401) {
        logoutUser();
        return null;
      }
      const data = await res.json();
      if (data) {
        setUser(data);
        userRef.current = data;
        setIsAuthenticated(true);
        localStorage.setItem("elUser", JSON.stringify(data));
        fetchUserRoles(); // Fetch roles after user info
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      logoutUser();
    }
  }, [fetchUserRoles, logoutUser]);

  const loginUser = useCallback(
    async (loginData) => {
      try {
        const res = await fetch(userRoute + "/authenticate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(loginData),
          credentials: "include",
        });
        const data = await res.json();
        if (data.message === "Authenticated") {
          fetchUserDetails();
        } else {
          console.warn("Invalid login attempt");
          return data.message;
        }
      } catch (error) {
        console.error("Failed to login:", error);
        return "Failed to access login server";
      }
    },
    [fetchUserDetails]
  );

  const refreshToken = useCallback(async () => {
    try {
      const res = await fetch(userRoute + "/refresh-token", {
        method: "POST",
        credentials: "include",
      });
      if (res.status === 401) {
        logoutUser();
        return false;
      }
      const data = await res.json();
      if (data?.message === "Token refreshed") {
        //fetchUserDetails();
        return true;
      }
      else{
        logoutUser();
        return false;
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
      logoutUser();
      return false;
    }
  }, [logoutUser]);

  const registerUser = useCallback(async (registerData) => {
    try {
      const res = await fetch(userRoute + "/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(registerData),
      });
      return res.json();
    } catch (error) {
      console.error("Registration failed:", error);
      return null;
    }
  }, []);

  const forgotPassword = useCallback(async (email) => {
    try {
      const res = await fetch(userRoute + "/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(email),
      });
      return res.json();
    } catch (error) {
      console.error("Forgot password failed:", error);
      return null;
    }
  }, []);

  const verifyUser = useCallback(async (token) => {
    try {
      const res = await fetch(userRoute + `/verify?token=${token}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      return res.json();
    } catch (error) {
      console.error("User verification failed:", error);
      return null;
    }
  }, []);

  const resetPassword = useCallback(async (data) => {
    try {
      const res = await fetch(userRoute + `/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return res.json();
    } catch (error) {
      console.error("Reset password failed:", error);
      return null;
    }
  }, []);

  useEffect(() => {
  const init = async () => {
    if (isAuthenticated) {
      const refreshed = await refreshToken(); // Add return value
      if (refreshed !== false) {
        await fetchUserDetails();
      }
    }
  };

  init();
}, [fetchUserDetails, isAuthenticated, refreshToken]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => {
      refreshToken();
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refreshToken, isAuthenticated]);

  const contextValue = useMemo(
    () => ({
      user,
      securityRoles,
      isAuthenticated,
      loginUser,
      logoutUser,
      registerUser,
      verifyUser,
      forgotPassword,
      resetPassword,
    }),
    [
      user,
      securityRoles,
      isAuthenticated,
      loginUser,
      logoutUser,
      registerUser,
      verifyUser,
      forgotPassword,
      resetPassword,
    ]
  );

  return (
    <AccessDataContext.Provider value={contextValue}>
      {children}
    </AccessDataContext.Provider>
  );
};

export default AccessDataProvider;